import React, { useContext } from "react";
import { useAppDispatch, useAppSelector, useIsMobile } from "../../app/hooks";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  selectLabels,
  selectLanguage,
  setLanguage,
} from "../../slices/language";
import useMenu from "../../utils/menu";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import "./mobile-layout.css";
import IconifyIcon from "../IconifyIcon";
import Logo from "../Logo";
import UserModal from "../Modals/UserModal";
import { resetToken } from "../../slices/token";
import {
  resetUser,
  selectUser,
  selectUserRole,
  setUser,
} from "../../slices/user";
import UserQrCode from "../UserQrCode";
import { showError } from "../../utils/helpers";
import api, { CreateUserPayload } from "../../api";
import { Context } from "../../context/ContextProvider/ContextProvider";
import { getPropertyLogoUrl } from "../../utils/getPropertyLogoUrl";

const MobileLayout = () => {
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const labels = useAppSelector(selectLabels);
  const currentLanguage = useAppSelector(selectLanguage);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { fixedMenu, toggleSubMenu, openSubmenu } = useMenu();

  const userInitial = user?.name.toUpperCase().charAt(0);
  const property = user?.property;
  const propertyImage = getPropertyLogoUrl(property);
  const hasQrCode =
    userRole === "host" ||
    userRole === "admin" ||
    userRole === "secondary_host";

  const [selectedLanguage, setSelectedLanguage] = React.useState<string>(
    currentLanguage.includes("es") ? "es" : "en"
  );
  const [showUserDetails, setShowUserDetails] = React.useState<boolean>(false);

  const languajes = [
    {
      label: labels.spanish,
      value: "es",
    },
    {
      label: labels.english,
      value: "en",
    },
  ];

  const { selectedAccessPointGuard, setSelectedAccessPointGuard } =
    useContext(Context);

  const accessPoints = React.useMemo(() => {
    if (user && user?.access_points) {
      const fixedAccessPoints = user?.access_points.map((item) => {
        return {
          label: item?.name,
          value: item?.id,
        };
      });
      return fixedAccessPoints;
    }
    return [];
  }, [user]);

  React.useEffect(() => {
    if (user?.last_access_selected_id && userRole === "guard") {
      setSelectedAccessPointGuard(`${user?.last_access_selected_id}`);
    }
  }, [setSelectedAccessPointGuard, user?.last_access_selected_id, userRole]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value as string;
    console.log("newLanguage", newLanguage);
    setSelectedLanguage(newLanguage);
    dispatch(setLanguage(newLanguage));
  };

  const onLogout = () => {
    dispatch(resetToken());
    dispatch(resetUser());
    navigate("/");
  };

  const updateLastAccessPoint = async (selectedValue: number) => {
    if (selectedValue && user) {
      const payload: CreateUserPayload = {
        last_access_selected_id: selectedValue,
      };
      try {
        const response = await api.updateUser(user?.id, payload);
        if (response) {
          const currentUser = {
            ...user,
            last_access_selected_id: selectedValue,
          };
          dispatch(setUser(currentUser));
        }
      } catch (error) {
        showError(dispatch, labels.generic_error);
      }
    }
  };

  return (
    <div className="w-100 flex" style={{ position: "relative" }}>
      {/* {isMobile ? (
        <div
          className="w-100 flex flex-dir-col align-items-center justify-content-center"
          style={{ height: "100vh" }}
        >
          <Logo type={"passTrackLogo"} alt="passtrack" className="logo" />
          <p className="main-title mt-12">{"Vista en proceso"}</p>
          <IconifyIcon icon={"material-symbols-light:construction"} className="title-color mb-8" style={{ fontSize: 60 }} />
          <Button onClick={() => onLogout()}>Login</Button>
        </div>
      ) : ( */}
      <>
        <UserModal
          showUserDetails={showUserDetails}
          setShowUserDetails={setShowUserDetails}
          onLogout={onLogout}
        />
        <div className="mobile-header">
          {userRole !== "guard" ? (
            propertyImage ? (
              <img
                src={propertyImage}
                alt={
                  property?.name ? `${property.name} logo` : "Property logo"
                }
                className="mobile-logo"
              />
            ) : (
              <Logo
                type={"passTrackLogo"}
                alt="passtrack"
                className="mobile-logo"
              />
            )
          ) : (
            <div style={{ width: 200 }}>
              <FormControl fullWidth size="small">
                {/* <InputLabel id="access_points">{labels.access_point}</InputLabel> */}
                <Select
                  labelId={"access_points"}
                  id={"access_points"}
                  // label={labels.access_point}
                  value={selectedAccessPointGuard}
                  onChange={(event: SelectChangeEvent) => {
                    const newSelectedAccessPoint = event.target.value as string;
                    setSelectedAccessPointGuard(newSelectedAccessPoint);
                    updateLastAccessPoint(Number(newSelectedAccessPoint));
                  }}
                >
                  {accessPoints.map((item, index) => (
                    <MenuItem
                      key={`${index}-${item?.value}`}
                      value={item?.value}
                    >
                      {item?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
          <div className="flex align-items-center gap-6">
            {hasQrCode && (
              <div className="user-init-container">
                <UserQrCode />
              </div>
            )}
            <div
              className="user-init-container"
              onClick={() => setShowUserDetails(true)}
            >
              <IconifyIcon
                icon={"fa-solid:user-circle"}
                fontSize={28}
                className="primary-color"
              />
            </div>
          </div>
        </div>
        <nav
          className="lower-menu p-8 border-box"
          style={{
            position: "fixed",
            boxShadow: "rgba(0, 0, 0, 0.24) 0px 0px 8px",
          }}
        >
          {fixedMenu.map((item, index) => {
            if (item.show) {
              return (
                <div
                  key={`${index}-${item?.label}`}
                  className="links-container"
                >
                  <Link to={item?.route} style={{ textDecoration: "none" }}>
                    <div
                      className={`link ${
                        location.pathname === item?.route ? "selected" : ""
                      }`}
                    >
                      <IconifyIcon icon={item?.icon} fontSize={26} />
                      <p className="font-12 font-w-500 select-none mt-2">
                        {item?.label}
                      </p>
                    </div>
                  </Link>
                </div>
              );
            }
            return null;
          })}
        </nav>
        <div className="mobile-content-container">
          <Outlet />
        </div>
      </>
      {/* )} */}
    </div>
  );
};

export default MobileLayout;
